/// <amd-module name="Repos/Views/CommitHub/CommitPermissionService" />
import { GitPullRequest, GitRepository, GitVersionDescriptor, GitVersionOptions, GitVersionType } from "Repos/Common/Contracts/Git";
import { GitRepositoryPermissions, IReposPermissionsService } from "Repos/Common/Permissions";
import { refToBranchName } from "Repos/Common/Util/Ref";
import { IVssPageContext, IVssService, Services, VssService } from "VSS/Platform/Context";

export const enum CommitPermissions {
    AddEditComment,
    CherryPickRevert,
    CreateBranch,
    CreateTag,
    UpdateCommentStatus
}

export interface ICommitPermissionService extends IVssService {
    /**
     * Determine whether the current user has specified commit permission.
     *
     * @param checkedPermission Pull request permission to check
     */
    hasPermission(checkedPermission: CommitPermissions): boolean;

    /**
     * Sets the permissions for the given commit.
     *
     * @param repository Repository to check permissions against
     */
    setPermisssions(repository: GitRepository): void;
}

class CommitPermissionService extends VssService implements ICommitPermissionService {
    private permissions = new Map<CommitPermissions, boolean>();

    public hasPermission(checkedPermission: CommitPermissions): boolean {
        if (!this.permissions.has(checkedPermission)) {
            throw new Error("Unknown permission. Make sure permissions are set.");
        }

        return this.permissions.get(checkedPermission)!;
    }

    public setPermisssions(repository: GitRepository): void {
        const { permissions } = this;

        permissions.clear();

        const permissionsService = this.pageContext.getService<IReposPermissionsService>("IReposPermissionsService");

        const canCreateBranch = this.canCreateBranch(permissionsService, repository);
        const canCreateTag = this.canCreateTag(permissionsService, repository);
        const canContribute = this.canContribute(permissionsService, repository);
        const canContributeToPullRequest = this.canContributeToPullRequest(permissionsService, repository);

        permissions.set(CommitPermissions.AddEditComment, canContribute);
        permissions.set(CommitPermissions.CherryPickRevert, canCreateBranch);
        permissions.set(CommitPermissions.CreateBranch, canCreateBranch);
        permissions.set(CommitPermissions.CreateTag, canCreateTag);
        permissions.set(CommitPermissions.UpdateCommentStatus, canContribute && canContributeToPullRequest);
    }

    private canContributeToPullRequest(permissionsService: IReposPermissionsService, repository: GitRepository): boolean {
        return permissionsService.hasPermission(GitRepositoryPermissions.PullRequestContribute, undefined, repository.id, repository.project.id);
    }

    private canCreateBranch(permissionsService: IReposPermissionsService, repository: GitRepository): boolean {
        return permissionsService.hasPermission(GitRepositoryPermissions.CreateBranch, undefined, repository.id, repository.project.id);
    }

    private canCreateTag(permissionsService: IReposPermissionsService, repository: GitRepository): boolean {
        return permissionsService.hasPermission(GitRepositoryPermissions.CreateTag, undefined, repository.id, repository.project.id);
    }

    private canContribute(permissionsService: IReposPermissionsService, repository: GitRepository): boolean {
        return permissionsService.hasPermission(GitRepositoryPermissions.GenericContribute, undefined, repository.id, repository.project.id);
    }
}

const serviceName = "ICommitPermissionService";

function getCommitPermissionService(pageContext: IVssPageContext): ICommitPermissionService {
    return pageContext.getService<ICommitPermissionService>(serviceName);
}

/**
 * Determine whether the current user has specified commit permission.
 *
 * @param pageContext Page context
 * @param checkedPermission Commit permission to check
 */
export function hasPermission(pageContext: IVssPageContext, checkedPermission: CommitPermissions): boolean {
    return getCommitPermissionService(pageContext).hasPermission(checkedPermission);
}

/**
 * Sets the permissions for the given commit.
 *
 * @param pageContext Page context
 * @param pullRequest Git repository
 */
export function setPermissions(pageContext: IVssPageContext, repository: GitRepository): void {
    getCommitPermissionService(pageContext).setPermisssions(repository);
}

Services.add(serviceName, { serviceFactory: CommitPermissionService });
